/**
 * Phone number formatter with country code support
 * Formats phone numbers as +(countrycode) based on country ISO code
 */

// Country code mapping: ISO 2-letter code -> Phone country code
const COUNTRY_PHONE_CODES: Record<string, string> = {
  // Balkans & Eastern Europe
  RS: "381", // Serbia
  ME: "382", // Montenegro
  MK: "389", // North Macedonia
  BA: "387", // Bosnia and Herzegovina
  HR: "385", // Croatia
  SI: "386", // Slovenia
  AL: "355", // Albania
  BG: "359", // Bulgaria
  RO: "40",  // Romania
  GR: "30",  // Greece
  HU: "36",  // Hungary
  SK: "421", // Slovakia
  CZ: "420", // Czech Republic
  PL: "48",  // Poland
  
  // Western Europe
  DE: "49",  // Germany
  FR: "33",  // France
  IT: "39",  // Italy
  ES: "34",  // Spain
  GB: "44",  // United Kingdom
  NL: "31",  // Netherlands
  BE: "32",  // Belgium
  AT: "43",  // Austria
  CH: "41",  // Switzerland
  SE: "46",  // Sweden
  NO: "47",  // Norway
  DK: "45",  // Denmark
  FI: "358", // Finland
  
  // Other regions
  US: "1",   // United States
  CA: "1",   // Canada
  AU: "61",  // Australia
  NZ: "64",  // New Zealand
  BR: "55",  // Brazil
  RU: "7",   // Russia
  TR: "90",  // Turkey
  UA: "380", // Ukraine
  CN: "86",  // China
  IN: "91",  // India
  JP: "81",  // Japan
  KR: "82",  // South Korea
  IL: "972", // Israel
  AE: "971", // UAE
  SA: "966", // Saudi Arabia
  EG: "20",  // Egypt
  ZA: "27",  // South Africa
};

/**
 * Extract digits only from phone number
 */
const extractDigits = (phone: string): string => {
  if (!phone) return "";
  return phone.replace(/\D/g, "");
};

/**
 * Check if phone number already starts with country code
 */
const hasCountryCode = (digits: string, countryCode: string): boolean => {
  if (!digits || !countryCode) return false;
  return digits.startsWith(countryCode);
};

/**
 * Remove country code prefix if present
 */
const removeCountryCode = (digits: string, countryCode: string): string => {
  if (!digits || !countryCode) return digits;
  if (digits.startsWith(countryCode)) {
    return digits.substring(countryCode.length);
  }
  return digits;
};

/**
 * Format phone number with country code based on country ISO code
 * @param phone - Phone number string (can include spaces, dashes, etc.)
 * @param country - Country ISO 2-letter code (e.g., "RS", "MK", "GR")
 * @returns Formatted phone number like "+381 12 345 6789" or original if can't format
 */
export const formatPhoneWithCountryCode = (
  phone: string | undefined | null,
  country: string | undefined | null
): string => {
  // Return N/A if no phone
  if (!phone || !phone.trim()) {
    return "N/A";
  }

  // Extract digits
  const digits = extractDigits(phone);
  if (!digits) {
    return phone; // Return original if no digits
  }

  // Normalize country code to uppercase
  const countryUpper = country ? country.toUpperCase().trim() : null;
  
  // Get phone country code if country is known
  const phoneCountryCode = countryUpper ? COUNTRY_PHONE_CODES[countryUpper] : null;

  if (phoneCountryCode) {
    // Check if phone already has country code
    if (hasCountryCode(digits, phoneCountryCode)) {
      // Already has country code, format it nicely
      const localNumber = removeCountryCode(digits, phoneCountryCode);
      return formatWithSpacing(`+${phoneCountryCode} ${localNumber}`, phoneCountryCode.length);
    } else {
      // Add country code
      return formatWithSpacing(`+${phoneCountryCode} ${digits}`, phoneCountryCode.length);
    }
  }

  // Fallback: Try to detect country code from phone number itself
  // Common patterns: phone might already start with country code
  if (digits.length >= 10) {
    // Try to match common country codes at the start
    for (const [isoCode, code] of Object.entries(COUNTRY_PHONE_CODES)) {
      if (digits.startsWith(code)) {
        const localNumber = removeCountryCode(digits, code);
        if (localNumber.length >= 6 && localNumber.length <= 10) {
          // Reasonable local number length
          return formatWithSpacing(`+${code} ${localNumber}`, code.length);
        }
      }
    }
  }

  // Last resort: If phone starts with +, assume it's already formatted
  if (phone.trim().startsWith("+")) {
    return formatInternationalNumber(phone.trim());
  }

  // Default: return with + prefix if it's a reasonable length
  if (digits.length >= 6 && digits.length <= 15) {
    return formatInternationalNumber(`+${digits}`);
  }

  // Return original if we can't format it
  return phone;
};

/**
 * Format phone number without spaces (as requested)
 */
const formatWithSpacing = (phone: string, countryCodeLength: number): string => {
  const parts = phone.split(" ");
  if (parts.length < 2) return phone.replace(/\s/g, "");
  
  const countryPart = parts[0]; // e.g., "+381"
  const numberPart = parts.slice(1).join(""); // All remaining digits
  
  // Return without spaces: +381123456789
  return `${countryPart}${numberPart}`;
};

/**
 * Format international number without spaces (as requested)
 */
const formatInternationalNumber = (phone: string): string => {
  // Remove all spaces and return with + prefix
  return phone.replace(/\s/g, "");
};

/**
 * Get country phone code for a given country ISO code
 */
export const getCountryPhoneCode = (country: string | undefined | null): string | null => {
  if (!country) return null;
  const countryUpper = country.toUpperCase().trim();
  return COUNTRY_PHONE_CODES[countryUpper] || null;
};

/**
 * Format phone for display so it always starts with 0 when applicable (e.g. Serbia local format).
 * For RS: 381xxx -> 0xxx, 9 digits -> 0xxx, already 0 -> as is.
 */
export const formatPhoneWithLeadingZero = (phone: string | undefined | null): string => {
  if (!phone || !String(phone).trim()) return "N/A";
  const digits = String(phone).replace(/\D/g, "");
  if (!digits) return String(phone);
  if (digits.startsWith("381")) return "0" + digits.substring(3);
  if (digits.startsWith("0")) return digits;
  if (digits.length === 9) return "0" + digits;
  return digits;
};

